<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import RewardFormBlock from '@/views/reward/components/RewardFormBlock.vue'
import LegacyRewardModal from '@/views/reward/components/RewardModal.vue'
import NewRewardModal from '@/components/rewards/RewardModal.vue'
import OfferRewardModal from '@/views/offer/components/OfferRewardModal.vue'

import {
  getValidityInDaysDisclaimer,
  normalizeMerchantRewardSavePayload
} from '@/components/rewards/utils/rewardHelpers'
import { hydrateItemsFromExternalId } from '@/components/rewards/utils/offerRewardHydration'

import type { LoyaltyReward } from '@/types/types'
import { useRewardForm } from '@/components/rewards/compmosable/useRewardForm'

const { t, d } = useI18n()

const props = withDefaults(
  defineProps<{
    selectedRewardId?: number | null
    isSelectionModalOpen: boolean
    isCreateEditRewardModalOpen: boolean
    rewards: LoyaltyReward[]
    usage?: 'marketing_flow' | 'offer'
    showValidityInDays?: boolean
    validityInDays?: number
    showPoints?: boolean
  }>(),
  {
    showPoints: false,
    usage: 'marketing_flow'
  }
)

const emit = defineEmits<{
  (e: 'handleSelectReward', rewardId: number): void
  (e: 'closeModal'): void
}>()

const selectedRewardId = useVModel(props, 'selectedRewardId')
const isSelectionModalOpen = useVModel(props, 'isSelectionModalOpen')
const isCreateEditRewardModalOpen = useVModel(props, 'isCreateEditRewardModalOpen')
const validityInDays = useVModel(props, 'validityInDays')

const initialRewardId = ref<number | null>(props.selectedRewardId)

const notification = useNotification()
const { saveReward } = useRewardForm()

const { flags } = useFeatureFlag()

const RewardModal = computed(() => {
  return flags.value['offer-engine']?.enabled ? NewRewardModal : LegacyRewardModal
})

const selectedReward = computed(() => props.rewards.find((reward) => reward.id === selectedRewardId.value))

const handleSelectReward = (rewardId: number) => {
  selectedRewardId.value = rewardId

  emit('handleSelectReward', rewardId)

  isSelectionModalOpen.value = false
  isCreateEditRewardModalOpen.value = false
}

const handleCreateReward = () => {
  initialRewardId.value = selectedRewardId.value
  selectedRewardId.value = null
  isSelectionModalOpen.value = false
  isCreateEditRewardModalOpen.value = true
}

const handleCloseEditModal = () => {
  if (initialRewardId.value) {
    selectedRewardId.value = initialRewardId.value
  }

  isCreateEditRewardModalOpen.value = false
}

const buildPayload = (reward: LoyaltyReward) => {
  if (!reward) return null

  const payload: any = {
    id: reward.id || null,
    rewardTypeId: reward.rewardType?.id || 1,
    name: reward.name,
    description: reward.description?.trim() || null,
    value: reward.value,
    externalId: reward.externalId,
    items: reward.items,
    source: reward.source || 'marketing_flow',

    voucherDiscountAmount: reward.voucherDiscountAmount || null,
    discountValuePercent: reward.discountValuePercent || null,
    minimumOrderAmount: reward.minimumOrderAmount || null
  }

  if (reward.minimumOrderAmount !== undefined) {
    payload.minimumOrderAmount = reward.minimumOrderAmount
  }

  if (reward.isForSingleUse !== undefined) {
    payload.isForSingleUse = reward.isForSingleUse
  }

  if (reward.pictureUrl !== null) {
    payload.pictureUrl = reward.pictureUrl
  } else {
    delete payload.pictureUrl
  }

  if (flags.value['offer-engine']?.enabled) {
    hydrateItemsFromExternalId(reward)
    normalizeMerchantRewardSavePayload(payload, reward)
  }

  return payload
}

const updateReward = async (reward: LoyaltyReward) => {
  if (props.usage === 'offer') {
    if (!reward?.id) {
      return
    }
    handleSelectReward(reward.id)
    return
  }

  try {
    if (!reward) {
      notification.create('error', {
        text: t('pages.reward.errors.rewardNotFound')
      })
      return
    }

    const payload = buildPayload(reward)
    if (!payload) return

    const savedReward = await saveReward(payload)

    if (reward.id) {
      notification.create('success', {
        text: t('pages.reward.editReward.success')
      })
    } else {
      notification.create('success', {
        text: t('pages.reward.createReward.success')
      })
    }

    handleSelectReward(savedReward.id)
  } catch {
    notification.create('error', {
      text: t('pages.reward.editReward.error')
    })
  }
}

const isFidelitySettingTouched = ref(false)

const validityInDaysDisclaimer = computed(() => {
  return getValidityInDaysDisclaimer(validityInDays.value, d, t)
})

const handleOfferRewardUpdated = (reward: LoyaltyReward) => {
  if (!reward?.id) return
  updateReward(reward)
}
</script>

<template>
  <RewardSelectionModal
    :is-open="isSelectionModalOpen"
    :rewards="rewards"
    show-edit-button
    :show-points="showPoints"
    :allow-offer-rewards="usage === 'offer'"
    @close-modal="() => (isSelectionModalOpen = false)"
    @create-reward="handleCreateReward"
    @select-reward="handleSelectReward"
  />

  <RewardModal
    v-if="usage === 'marketing_flow' && isCreateEditRewardModalOpen"
    :is-external-form-modified="isFidelitySettingTouched"
    :reward-id="selectedReward?.id"
    usage="marketing_flow"
    hide-claim-limit-block
    @close="handleCloseEditModal"
    @update:reward="updateReward"
  >
    <template v-if="showValidityInDays" #additional-form-blocks>
      <RewardFormBlock :title="t('pages.reward.detail.marketingFlowOptions')">
        <div>
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.loyaltyPage.edit.fields.validityInDays') }}</span>
          </label>

          <AppFormInput
            id="validityInDays"
            v-model="validityInDays"
            type="number"
            step="1"
            title="validityInDays"
            placeholder="∞"
            :min="0"
            :max="365"
            @update:model-value="isFidelitySettingTouched = true"
          >
            <template #icon-right>
              <div class="text-sm text-muted-foreground h-5 flex items-center justify-center">
                {{ t('global.words.days') }}
              </div>
            </template>
          </AppFormInput>

          <p class="text-xs text-muted-foreground mt-1">{{ validityInDaysDisclaimer }}</p>

          <BaseTip
            severity="info"
            class="mt-4"
            :text="t('pages.marketingFlow.marketingFlowEditPage.action.infoRewardValidity')"
          />
        </div>
      </RewardFormBlock>
    </template>
  </RewardModal>

  <OfferRewardModal
    v-else-if="usage === 'offer' && isCreateEditRewardModalOpen"
    :reward-id="selectedReward?.id"
    @close="handleCloseEditModal"
    @update:reward="handleOfferRewardUpdated"
  />
</template>
